// Admin.js — Pinterest-like Masonry (STYLE ONLY)
// ✅ Goals (per request):
//  - Keep the masonry layout
//  - Use FULL PAGE WIDTH (remove left padding reserved for sidebar)
//  - Show media in ORIGINAL proportions (NO ZOOM / NO CROP)
//  - Keep pins balanced: very tall portrait VIDEOS get ~half-height tiles (still full video visible)
//  - Never show white frames
// ⚠️ Business logic preserved: endpoints, paging, caching, filters, finals, users avatars

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "./AuthContext";

const PAGE_SIZE = 40;

const WV_LANG_KEY = "weavevo_lang";

/**
 * Minimal Admin i18n.
 * Default language: English ("en")
 * Optional: French ("fr")
 */
const ADMIN_I18N = {
  en: {
    // Filters
    filter_all: "All",
    filter_open: "Open threads",
    filter_finals: "Finished Weaves",

    // Open threads sub-filter
    open_kind_all: "All",
    open_kind_video: "Video",
    open_kind_image: "Image",

    // Common UI
    loading_short: "Loading…",
    loading_media: "Loading media…",
    no_media: "No media",
    scroll_more: "Scroll to load more",
    end_of_list: "End of list",

    // Labels / badges
    visibility_public: "Public",
    visibility_private: "Private",
    kind_video: "Video",
    kind_image: "Image",
    kind_file: "File",
    kind_final: "Final",

    // Accessibility / misc
    open_aria: "Open {title}",
    generic_media: "media",
    profile: "Profile",
    profile_title: "Profile: {name}",
    creator: "Creator",
    media_details: "Media details",
    reactions_comments: "Reactions and comments",
    contributors_count: "{count} contributors",
  },

  fr: {
    // Filters
    filter_all: "Tous",
    filter_open: "Threads ouverts",
    filter_finals: "Weaves Finalisés",

    // Sous-filtre "Threads ouverts"
    open_kind_all: "Tout",
    open_kind_video: "Vidéo",
    open_kind_image: "Image",

    // Common UI
    loading_short: "Chargement…",
    loading_media: "Chargement des médias…",
    no_media: "Aucun média",
    scroll_more: "Faites défiler pour charger plus",
    end_of_list: "Fin de la liste",

    // Labels / badges
    visibility_public: "Public",
    visibility_private: "Privé",
    kind_video: "Vidéo",
    kind_image: "Image",
    kind_file: "Fichier",
    kind_final: "Final",

    // Accessibility / misc
    open_aria: "Ouvrir {title}",
    generic_media: "média",
    profile: "Profil",
    profile_title: "Profil : {name}",
    creator: "Créateur",
    media_details: "Détails média",
    reactions_comments: "Réactions et commentaires",
    contributors_count: "{count} contributeurs",
  },
};

// Cache TTL (client-side)
const CACHE_TTL_MS = 3 * 60 * 1000;

// Module-scope caches (persist across mounts)
const ADMIN_AGG_CACHE = new Map(); // key -> { items, offset, hasMore, ts }
const ADMIN_PAGE_CACHE = new Map(); // key -> { page, ts }

// ✅ Memorize measured aspect ratios to avoid layout “decallage” when coming back / scrolling
const MEDIA_AR_CACHE = new Map(); // src -> aspectRatio (width/height)

// ✅ NEW: contributors cache for FINAL project cards (Réalisations)
const PROJECT_CONTRIB_CACHE = new Map(); // projectId -> { list: string[], ts }
const PROJECT_CONTRIB_INFLIGHT = new Map(); // projectId -> Promise<string[]>

// ✅ DEMO: add extra fake contributors (UI-only) to show big collaboration
// (Does NOT touch DB / business logic — only enriches the visual overlay)
const DEMO_FIRST_NAMES = [
  "Nora",
  "Yasmine",
  "Amine",
  "Sami",
  "Lina",
  "Rania",
  "Hugo",
  "Emma",
  "Noah",
  "Léa",
  "Mehdi",
  "Inès",
  "Adam",
  "Sara",
  "Omar",
  "Maya",
  "Rayan",
  "Nina",
  "Youssef",
  "Aya",
];

const DEMO_LAST_NAMES = [
  "Martin",
  "Bernard",
  "Dubois",
  "Thomas",
  "Petit",
  "Robert",
  "Richard",
  "Durand",
  "Moreau",
  "Laurent",
  "Simon",
  "Michel",
  "Lefebvre",
  "Garcia",
  "Roux",
  "Fournier",
  "Girard",
  "Lambert",
  "Ben Ali",
  "Trabelsi",
];

function wvHash32(str) {
  // FNV-1a 32-bit
  const s = String(str || "");
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function wvMulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function wvInt(rand, min, max) {
  const r = rand();
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return Math.floor(r * (hi - lo + 1)) + lo;
}

function wvPick(rand, arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr[wvInt(rand, 0, arr.length - 1)];
}

function wvDemoAvatarUrl(rand, used) {
  // pravatar has ~70 faces. Keep unique per card.
  for (let i = 0; i < 120; i += 1) {
    const imgId = wvInt(rand, 1, 70);
    if (!used.has(imgId)) {
      used.add(imgId);
      return `https://i.pravatar.cc/120?img=${imgId}`;
    }
  }
  // fallback (non-face)
  return `https://ui-avatars.com/api/?name=${encodeURIComponent("Demo")}&background=dee2e6&color=495057`;
}

/**
 * Lazy media loader: loads near viewport.
 * ✅ Keeps FULL media visible (no crop) with the ORIGINAL aspect ratio (no zoom).
 * ✅ For portrait VIDEOS: reduce preview tile height ~50% (still full video visible via object-fit: contain)
 */
function LazyMediaVisual({ isImage, src, title, isVideo }) {
  const holderRef = useRef(null);
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  // aspectRatio = width / height
  const [aspectRatio, setAspectRatio] = useState(() => {
    const cached = MEDIA_AR_CACHE.get(src);
    return typeof cached === "number" && Number.isFinite(cached) && cached > 0 ? cached : 4 / 3;
  });

  const setARFromWH = useCallback(
    (w, h) => {
      const W = Number(w);
      const H = Number(h);
      if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) return;

      const r = W / H;

      // Keep original ratio (NO CLAMP). Just ignore corrupted extremes.
      if (!Number.isFinite(r) || r <= 0.05 || r >= 50) return;

      MEDIA_AR_CACHE.set(src, r);
      setAspectRatio(r);
    },
    [src]
  );

  // ✅ Display aspect ratio (tile) — for portrait VIDEOS only, halve the height by doubling w/h ratio.
  const displayAspectRatio = useMemo(() => {
    const r = aspectRatio;
    if (!Number.isFinite(r) || r <= 0) return r;

    // ✅ For very tall portrait media (image OR video): halve the tile height (still full media visible via object-fit: contain)
    if (r < 0.62) {
      return r * 2;
    }

    // ✅ Keep previous behavior for portrait videos (moderate portrait): make tiles less tall
    if (isVideo && r < 1) {
      return r * 2;
    }

    return r;
  }, [aspectRatio, isVideo]);

  // ✅ Extra cap for very tall portrait tiles (prevents disproportionate heights in masonry)
  const tileMaxH = useMemo(() => {
    const r = aspectRatio;
    if (!Number.isFinite(r) || r <= 0) return 420;
    return r < 0.62 ? 320 : 420;
  }, [aspectRatio]);


  // ✅ Play videos ONLY on hover (pause/reset on leave)
  const playOnHover = useCallback(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;

    // Start near 0 to show a real frame quickly
    try {
      const dur = Number(v.duration);
      if (Number.isFinite(dur) && dur > 0) v.currentTime = Math.min(0.1, dur);
      else v.currentTime = 0;
    } catch {
      // ignore
    }

    try {
      const p = v.play?.();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      // ignore
    }
  }, [isVideo]);

  const stopOnLeave = useCallback(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;

    try {
      v.pause?.();
    } catch {
      // ignore
    }

    try {
      const dur = Number(v.duration);
      if (Number.isFinite(dur) && dur > 0) v.currentTime = Math.min(0.1, dur);
      else v.currentTime = 0;
    } catch {
      // ignore
    }
  }, [isVideo]);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "700px 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={holderRef}
      className={`wv-pin-thumb-wrap ${isVideo ? "wv-pin-is-video" : ""}`}
      style={{ aspectRatio: displayAspectRatio, '--wv-pin-maxh': `${tileMaxH}px` }}
      onMouseEnter={playOnHover}
      onMouseLeave={stopOnLeave}
    >
      {!shouldLoad ? (
        <div className="wv-pin-skeleton" />
      ) : isImage ? (
        <img
          src={src}
          alt={title || "media"}
          className="wv-pin-thumb"
          loading="lazy"
          decoding="async"
          style={{ objectFit: "contain", objectPosition: "center" }}
          onLoad={(e) => {
            const img = e.currentTarget;
            setARFromWH(img?.naturalWidth, img?.naturalHeight);
          }}
        />
      ) : (
        <video
          ref={videoRef}
          src={src}
          preload="metadata"
          className="wv-pin-thumb"
          muted
          playsInline
          loop
          style={{ objectFit: "contain", objectPosition: "center" }}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            setARFromWH(v?.videoWidth, v?.videoHeight);

            // Force an early frame (helps previews for some browsers)
            try {
              if (typeof v?.currentTime === "number") v.currentTime = Math.min(0.1, v.duration || 0.1);
            } catch {
              // ignore
            }
          }}
        />
      )}

      {/* ✅ Center icon: video vs photo */}
      {shouldLoad ? (
        <div className="wv-pin-typeIcon" aria-hidden="true">
          <i className={isVideo ? "bi bi-play-circle-fill" : "bi bi-image"} />
        </div>
      ) : null}
    </div>
  );
}

export default function Admin() {
  const { token } = useAuth(); // kc_token


  // Language: default EN. Settings page can set localStorage(WV_LANG_KEY) to "fr".
  const [lang, setLang] = useState(() => {
    try {
      const raw = localStorage.getItem(WV_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
      const norm = String(raw || "").toLowerCase().slice(0, 2);
      return norm === "fr" ? "fr" : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(WV_LANG_KEY) || localStorage.getItem("weavevo_language") || "";
        const norm = String(raw || "").toLowerCase().slice(0, 2);
        const next = norm === "fr" ? "fr" : "en";
        setLang(next);
        if (!raw) localStorage.setItem(WV_LANG_KEY, next);
      } catch {}
    };

    read();
    window.addEventListener("storage", read);
    window.addEventListener("focus", read);

    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("focus", read);
    };
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = ADMIN_I18N?.[lang] || ADMIN_I18N?.en || {};
      let out = dict[key] ?? ADMIN_I18N?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [lang]
  );

  const locale = lang === "fr" ? "fr-FR" : "en-US";

  // Main list is paged
  const [mediaList, setMediaList] = useState([]);

  const [filter, setFilter] = useState("all");

  // Open threads can be filtered by media kind (all/video/image)
  const [openKind, setOpenKind] = useState("all");
  const [contributorFilter, setContributorFilter] = useState("");
  const [finals, setFinals] = useState([]);

  // Paging state
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const loadingRef = useRef(false);

  // ✅ Topbar dynamic spacer to avoid cut/overlap on any screen width/zoom
  const topbarRef = useRef(null);
  const [topbarH, setTopbarH] = useState(76);

  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const update = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (!h) return;
      setTopbarH((prev) => (prev === h ? prev : h));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 👤 Users / avatars
  const [usersByUsername, setUsersByUsername] = useState({});
  const [avatarBust, setAvatarBust] = useState(0);

  // ✅ NEW: contributors per project (FINAL cards)
  const [projectContribsById, setProjectContribsById] = useState({});
  const projectContribsRef = useRef({});
  useEffect(() => {
    projectContribsRef.current = projectContribsById;
  }, [projectContribsById]);

  // Refresh avatars when profile is updated (same event used in Navbar.js)
  useEffect(() => {
    const onProfileUpdated = () => setAvatarBust(Date.now());
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  const appendBust = (url) => {
    if (!url) return url;
    if (!avatarBust) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${avatarBust}`;
  };

  const normalizeUploadPath = (p) => {
    let s = String(p || "").trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/uploads/")) return s;
    while (s.startsWith("/")) s = s.slice(1);
    return "/uploads/" + s;
  };

  const avatarFor = (username, labelForFallback = "") => {
    const u = usersByUsername[username];

    // ✅ Prefer avatar_url like Navbar.js
    const avatarUrl = u?.avatar_url || u?.avatarUrl || u?.avatar || u?.photo_url;
    if (avatarUrl) return appendBust(String(avatarUrl));

    // ✅ Fallback to stored profilepath
    const profilePath = u?.profilepath || u?.profilePath || u?.profile_path;
    if (profilePath) return appendBust(normalizeUploadPath(profilePath));

    // ✅ Last fallback (better than default png)
    const fallbackName = labelForFallback || username || "User";
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}&background=dee2e6&color=495057`;
  };

  const getMediaType = (fileName) => {
    const ext = (fileName || "").split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
    if (["mp4", "webm", "ogg", "mov", "mkv"].includes(ext)) return "video";
    return "file";
  };

  const getTypeParamFromFilter = (f) => {
    // "Open threads" can optionally be narrowed to video/image (without creating extra top-level filters)
    if (f === "open") {
      if (openKind === "video") return "video";
      if (openKind === "image") return "image";
      return null; // all kinds
    }
    return null;
  };

  // ✅ Sorting helpers
  const getCreatedTs = (it) => {
    const raw = it?.created_at ?? it?.createdAt ?? it?.date ?? it?.eventdate;
    if (raw == null) return 0;

    if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;

    const s = String(raw).trim();
    if (!s) return 0;

    // numeric timestamps (ms)
    if (/^[0-9]{11,}$/.test(s)) {
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    }

    const d = new Date(s);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const formatDate = (raw) => {
  if (raw == null) return "";

  const t = new Date(raw).getTime();
  if (!Number.isFinite(t)) return "";

  const now = Date.now();
  const diffMs = now - t;
  const isFuture = diffMs < 0;

  const absSec = Math.floor(Math.abs(diffMs) / 1000);
  if (absSec < 5) {
    return lang === "fr" ? "à l’instant" : "just now";
  }

  const units = [
    { sec: 31536000, en: ["year", "years"], fr: ["an", "ans"] },
    { sec: 2592000,  en: ["month", "months"], fr: ["mois", "mois"] },
    { sec: 604800,   en: ["week", "weeks"], fr: ["semaine", "semaines"] },
    { sec: 86400,    en: ["day", "days"], fr: ["jour", "jours"] },
    { sec: 3600,     en: ["hour", "hours"], fr: ["heure", "heures"] },
    { sec: 60,       en: ["minute", "minutes"], fr: ["minute", "minutes"] },
    { sec: 1,        en: ["second", "seconds"], fr: ["seconde", "secondes"] },
  ];

  const u = units.find((x) => absSec >= x.sec) || units[units.length - 1];
  const value = Math.max(1, Math.floor(absSec / u.sec));

  const n = value.toLocaleString(locale);
  const label = (lang === "fr" ? u.fr : u.en)[value > 1 ? 1 : 0];

  if (isFuture) return lang === "fr" ? `dans ${n} ${label}` : `in ${n} ${label}`;
  return lang === "fr" ? `depuis ${n} ${label}` : `since ${n} ${label}`;
};

  // ✅ Build a stable cache key per server query
  const buildBaseKey = useCallback(() => {
    const typeParam = getTypeParamFromFilter(filter) || "all";
    return `type=${typeParam}|limit=${PAGE_SIZE}|light=1`;
  }, [filter, openKind]);

  const isFresh = (ts) => Date.now() - ts < CACHE_TTL_MS;

  const loadPage = useCallback(
    async ({ reset = false } = {}) => {
      // No need to fetch media pages when filter is "closed"
      if (filter === "closed") {
        setMediaList([]);
        setHasMore(false);
        return;
      }

      const baseKey = buildBaseKey();

      // ✅ Serve aggregate cache instantly on reset
      if (reset) {
        const cachedAgg = ADMIN_AGG_CACHE.get(baseKey);
        if (cachedAgg && isFresh(cachedAgg.ts)) {
          setMediaList(cachedAgg.items || []);
          offsetRef.current = cachedAgg.offset || (cachedAgg.items || []).length || 0;
          setHasMore(!!cachedAgg.hasMore);
          return;
        }
      }

      if (loadingRef.current) return;
      loadingRef.current = true;
      setIsLoading(true);

      try {
        const typeParam = getTypeParamFromFilter(filter);

        const offset = reset ? 0 : offsetRef.current;
        const pageKey = `${baseKey}|offset=${offset}`;

        // ✅ Serve page cache for "load more"
        const cachedPage = ADMIN_PAGE_CACHE.get(pageKey);
        if (cachedPage && isFresh(cachedPage.ts)) {
          const arr = cachedPage.page || [];

          if (reset) {
            setMediaList(arr);
            offsetRef.current = arr.length;
          } else {
            setMediaList((prev) => [...prev, ...arr]);
            offsetRef.current = offsetRef.current + arr.length;
          }

          const nextHasMore = arr.length === PAGE_SIZE;
          setHasMore(nextHasMore);

          // ✅ Update aggregate cache
          const newItems = reset ? arr : [...(ADMIN_AGG_CACHE.get(baseKey)?.items || []), ...arr];

          ADMIN_AGG_CACHE.set(baseKey, {
            items: newItems,
            offset: reset ? arr.length : offsetRef.current,
            hasMore: nextHasMore,
            ts: Date.now(),
          });

          return;
        }

        const params = new URLSearchParams();
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(offset));
        params.set("light", "1");
        if (typeParam) params.set("type", typeParam);

        const res = await fetch(`/media?${params.toString()}`);
        const data = res.ok ? await res.json() : [];
        const arr = Array.isArray(data) ? data : [];

        // ✅ Store page cache
        ADMIN_PAGE_CACHE.set(pageKey, { page: arr, ts: Date.now() });

        if (reset) {
          setMediaList(arr);
          offsetRef.current = arr.length;
        } else {
          setMediaList((prev) => [...prev, ...arr]);
          offsetRef.current = offsetRef.current + arr.length;
        }

        const nextHasMore = arr.length === PAGE_SIZE;
        setHasMore(nextHasMore);

        // ✅ Update aggregate cache
        const prevAgg = ADMIN_AGG_CACHE.get(baseKey);
        const merged = reset ? arr : [...(prevAgg?.items || []), ...arr];

        ADMIN_AGG_CACHE.set(baseKey, {
          items: merged,
          offset: merged.length,
          hasMore: nextHasMore,
          ts: Date.now(),
        });
      } catch (e) {
        if (reset) setMediaList([]);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
      }
    },
    [filter, buildBaseKey]
  );

  const resetAndLoad = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    setMediaList([]);
    loadPage({ reset: true });
  }, [loadPage]);

  // 🔄 Load medias (paged) when filter changes
  useEffect(() => {
    resetAndLoad();
  }, [filter, resetAndLoad]);

  // 🔄 Load projects → finals for closed ones
  useEffect(() => {
    fetch("/projects")
      .then((r) => (r.ok ? r.json() : []))
      .then((projects = []) => {
        const closed = (projects || []).filter((p) => {
          const s = (p.status || "").toLowerCase();
          return s.includes("clôturé") || s.includes("term");
        });

        const mapped = closed.map((p) => ({
          __kind: "project-final",
          id: `final-${p.id}`,
          projectId: p.id,
          title: p.title || "Projet",
          created_at: p.created_at,
          user: p.created_by || "",
          final_src: `/output/project_${p.id}_final.mp4`,
          visibility: p.visibility || "PUBLIC",
          likes_count: p.likes_count ?? p.reactions_count ?? p.likes ?? 0,
          comments_count: p.comments_count ?? p.comment_count ?? p.comments ?? 0,
        }));

        setFinals(mapped);
      })
      .catch(() => setFinals([]));
  }, []);

  // 🔄 Load users (for avatars)
  useEffect(() => {
    if (!token) return;

    fetch("/users", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) return [];
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) return [];
        return r.json();
      })
      .then((arr) => {
        const map = {};
        (arr || []).forEach((u) => {
          if (u?.username) map[u.username] = u;
        });
        setUsersByUsername(map);
        setAvatarBust(Date.now()); // force refresh once users are loaded
      })
      .catch(() => setUsersByUsername({}));
  }, [token]);

  // ✅ NEW: extract usernames from various API shapes (compatible with existing backends)
  const extractUsernames = useCallback((payload) => {
    const pickFromObj = (o) => {
      if (!o || typeof o !== "object") return null;
      return (
        o.username ||
        o.user ||
        o.user_name ||
        o.email ||
        o.mail ||
        o.created_by ||
        o.createdBy ||
        o.contributor ||
        o.owner ||
        o.login ||
        null
      );
    };

    const toList = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);

    let list = [];

    if (Array.isArray(payload)) {
      list = payload.map((x) => (typeof x === "string" ? x : pickFromObj(x))).filter(Boolean);
    } else if (payload && typeof payload === "object") {
      const keysToTry = [
        "contributors",
        "members",
        "users",
        "participants",
        "invited",
        "collaborators",
        "contribs",
        "contributors_list",
        "contributorsList",
      ];

      for (const k of keysToTry) {
        if (payload[k] != null) {
          list = toList(payload[k]).map((x) => (typeof x === "string" ? x : pickFromObj(x))).filter(Boolean);
          if (list.length) break;
        }
      }

      if (!list.length && payload.data) list = extractUsernames(payload.data);
      if (!list.length && payload.result) list = extractUsernames(payload.result);
    }

    const seen = new Set();
    const out = [];
    for (const raw of list) {
      const u = String(raw || "").trim();
      if (!u) continue;
      const key = u.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(u);
    }
    return out;
  }, []);

  // ✅ NEW: fetch contributors for a project (FINAL cards). Cached + inflight dedupe.
  const fetchProjectContributors = useCallback(
    async (projectId) => {
      const pid = String(projectId || "").trim();
      if (!pid) return [];

      const cached = PROJECT_CONTRIB_CACHE.get(pid);
      if (cached && isFresh(cached.ts)) return Array.isArray(cached.list) ? cached.list : [];

      if (PROJECT_CONTRIB_INFLIGHT.has(pid)) return PROJECT_CONTRIB_INFLIGHT.get(pid);

      const p = (async () => {
        const hdrAuth = token ? { Authorization: `Bearer ${token}` } : null;

        const tryJson = async (url, headers) => {
          const res = await fetch(url, headers ? { headers } : undefined);
          if (!res.ok) throw new Error(String(res.status));
          const ct = res.headers.get("content-type") || "";
          if (!ct.includes("application/json")) throw new Error("not-json");
          return res.json();
        };

        const enc = encodeURIComponent(pid);

        // Use routes that actually exist on the backend.
        // /projects/:id/media gives real contributors; /projects/:id gives the creator fallback.
        // Avoid probing legacy /contributors and /members routes because they generate noisy 404s.
        const attempts = [
          { url: `/projects/${enc}/media`, headers: hdrAuth },
          { url: `/projects/${enc}`, headers: hdrAuth },

          // fallback without auth (some installs expose meta publicly)
          { url: `/projects/${enc}/media`, headers: null },
          { url: `/projects/${enc}`, headers: null },
        ];

        for (const a of attempts) {
          try {
            const json = await tryJson(a.url, a.headers);
            const users = extractUsernames(json);
            if (users.length) return users;
          } catch {
            // keep trying
          }
        }

        return [];
      })();

      PROJECT_CONTRIB_INFLIGHT.set(pid, p);

      try {
        const list = await p;
        PROJECT_CONTRIB_CACHE.set(pid, { list, ts: Date.now() });
        return list;
      } finally {
        PROJECT_CONTRIB_INFLIGHT.delete(pid);
      }
    },
    [extractUsernames, isFresh, token]
  );

  // ✅ NEW: prefetch contributors for finals (small batches)
  useEffect(() => {
    const ids = Array.from(new Set((finals || []).map((f) => String(f?.projectId || "").trim()).filter(Boolean)));
    if (!ids.length) return;

    let cancelled = false;
    const known = projectContribsRef.current || {};
    const immediate = {};
    const toFetch = [];

    for (const pid of ids) {
      if (known[pid]) continue;

      const cached = PROJECT_CONTRIB_CACHE.get(pid);
      if (cached && isFresh(cached.ts) && Array.isArray(cached.list)) {
        immediate[pid] = cached.list;
      } else {
        toFetch.push(pid);
      }
    }

    if (Object.keys(immediate).length) {
      setProjectContribsById((prev) => ({ ...prev, ...immediate }));
    }

    if (!toFetch.length) return;

    const BATCH = 6;

    (async () => {
      for (let i = 0; i < toFetch.length; i += BATCH) {
        if (cancelled) break;
        const chunk = toFetch.slice(i, i + BATCH);

        const results = await Promise.all(
          chunk.map(async (pid) => {
            const list = await fetchProjectContributors(pid);
            return [pid, list];
          })
        );

        if (cancelled) break;

        const patch = {};
        for (const [pid, list] of results) patch[pid] = list;

        setProjectContribsById((prev) => ({ ...prev, ...patch }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [finals, fetchProjectContributors, isFresh]);

  const resolveProjectCreatorUsername = (m) => m?.project_created_by || m?.project_creator || m?.created_by || m?.owner || m?.user || "";

  const resolveProjectCreatorLabel = (m) => {
    const u = String(resolveProjectCreatorUsername(m) || "").trim();
    if (!u) return "—";
    return u.includes("@") ? u.split("@")[0] : u;
  };

  const resolveUserProfilePath = (username) => {
    const u = String(username || "").trim();
    if (!u) return null;
    return `/users/${encodeURIComponent(u)}`;
  };

  const toCamelCaseName = (s) => {
    const raw = String(s || "").trim();
    if (!raw) return "";
    return raw
      .split(/[^a-zA-Z0-9]+/g)
      .filter(Boolean)
      .map((w) => {
        const lower = String(w).toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(" ");
  };

  // ✅ DEMO: build a contributors stack for overlay (real + extra fake people)
  // - real contributors come from DB (via /users + project contributors endpoints)
  // - fake contributors are UI-only, deterministic per projectId (seeded), to look “busy” for demos
  const buildContribOverlayList = (projectId, realUsernames) => {
    const pid = String(projectId || "").trim();
    const base = Array.isArray(realUsernames) ? realUsernames : [];

    // Map real contributors -> avatar objects
    const out = base.map((u) => {
      const username = String(u || "").trim();
      const label = username.includes("@") ? username.split("@")[0] : username;
      return {
        key: `real-${username}`,
        label,
        avatarSrc: avatarFor(username, toCamelCaseName(label)),
        isDemo: false,
      };
    });

    // Add extra fake contributors (demo)
    const seed = wvHash32(`wv-demo:${pid}`);
    const rand = wvMulberry32(seed);

    // Random extra people, but keep stable per projectId
    const extra = wvInt(rand, 10, 24);
    const maxTotal = 40;
    const targetTotal = Math.max(out.length, Math.min(maxTotal, out.length + extra));
    const need = Math.max(0, targetTotal - out.length);

    const usedLabels = new Set(out.map((x) => String(x.label || "").toLowerCase()));
    const usedAvatarIds = new Set();

    for (let i = 0; i < need; i += 1) {
      let name = "";
      for (let tries = 0; tries < 40; tries += 1) {
        name = `${wvPick(rand, DEMO_FIRST_NAMES)} ${wvPick(rand, DEMO_LAST_NAMES)}`.trim();
        const key = name.toLowerCase();
        if (name && !usedLabels.has(key)) {
          usedLabels.add(key);
          break;
        }
      }

      if (!name) name = `Demo User ${i + 1}`;

      out.push({
        key: `demo-${pid}-${i}`,
        label: name,
        avatarSrc: wvDemoAvatarUrl(rand, usedAvatarIds),
        isDemo: true,
      });
    }

    return out;
  };

  /** ------------------------ Filtering + SORT DESC ------------------------ **/
  const filteredMedia = useMemo(() => {
    const contributorMatch = (username) =>
      contributorFilter ? (username || "").toLowerCase().includes(contributorFilter.toLowerCase()) : true;

    let base = [];

    if (filter === "closed") {
      // Weaves (finished projects)
      base = finals.filter((f) => contributorMatch(f.user));
    } else if (filter === "all") {
      // All = Weaves + open threads
      const medias = mediaList.filter((m) => contributorMatch(m.user));
      const fns = finals.filter((f) => contributorMatch(f.user));
      base = [...fns, ...medias];
    } else if (filter === "open") {
      // Open threads = regular medias (video/image/file), optionally narrowed by openKind
      base = mediaList.filter((media) => {
        if (!contributorMatch(media.user)) return false;

        if (openKind === "all") return true;

        const type = getMediaType(media.file_path || media.location);
        if (openKind === "video") return type === "video";
        if (openKind === "image") return type === "image";
        return true;
      });
    } else {
      // Fallback (should not happen)
      base = mediaList.filter((m) => contributorMatch(m.user));
    }

    // ✅ Sort everything by creation date DESC
    return [...base].sort((a, b) => getCreatedTs(b) - getCreatedTs(a));
  }, [filter, openKind, contributorFilter, finals, mediaList]);

  /** ------------------------ Infinite scroll sentinel ------------------------ **/
  const sentinelRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        if (isLoading || !hasMore) return;
        if (filter === "closed") return;
        loadPage({ reset: false });
      },
      { rootMargin: "900px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [filter, hasMore, isLoading, loadPage]);

  const filterOptions = useMemo(
    () => [
      { id: "all", label: t("filter_all"), icon: "bi bi-grip-horizontal" },
      { id: "open", label: t("filter_open"), icon: "bi bi-chat-left-text" },
      { id: "closed", label: t("filter_finals"), icon: "bi bi-patch-check-fill" },
    ],
    [t]
  );

  /** ------------------------ Styles (Pinterest-like) ------------------------ **/
  const styles = `

 /* NEW */
@media (max-width: 720px){
  .admin-pin.page-wrap{
    --wv-left-rail: 0px;
    --wv-content-pad: 12px;
    --wv-right-safe: 0px;

    padding-left: var(--wv-content-pad) !important;
    padding-right: var(--wv-content-pad) !important;
  }

  .admin-pin .wv-masonry{
    width: 100%;
    max-width: 100%;
  }
}

  .admin-pin, .admin-pin *, .admin-pin *::before, .admin-pin *::after{ box-sizing: border-box; }

  .admin-pin .admin-pin-content{ width: 100%; max-width: 100%; }

  /* Top bar */
 /* Top bar (FULL WIDTH) */
.admin-pin .wv-topbar{
  position: fixed;
  top: 70px;
  z-index: 1000;

  /* ✅ make the “green” background span the whole screen (minus left rail) */
  left: var(--wv-rail-w);
  right: 0;
  width: calc(100vw - var(--wv-rail-w));
  width: calc(100dvw - var(--wv-rail-w));
  max-width: calc(100vw - var(--wv-rail-w));
  box-sizing: border-box;

  background: rgba(250,250,250,.92);
  backdrop-filter: blur(10px);

  /* ✅ keep same inner spacing style as the page */
  padding: 10px var(--wv-content-pad);

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}


/* NEW (match MyMedia chips/search sizing) */
.admin-pin .wv-chip{
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  font-size: 13px;
  line-height: 1;
  border: 1px solid rgba(15,23,42,.08);
  background: #fff;
  color: #0f172a;
  transition: transform .12s ease, box-shadow .15s ease;
  white-space: nowrap;
}
.admin-pin .wv-chip:hover{ transform: translateY(-1px); box-shadow: 0 10px 20px rgba(15,23,42,.08); }
.admin-pin .wv-chip-active{ background: #111827; color: #fff; border-color: rgba(17,24,39,.45); }

/* NEW: sub-filter (Open threads: All / Video / Image) */
.admin-pin .wv-divider{
  width: 1px;
  height: 28px;
  background: rgba(15,23,42,.10);
  margin: 0 6px;
  align-self: center;
}
.admin-pin .wv-subchips{
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.admin-pin .wv-chip--sub{
  padding: 8px 10px;
  font-size: 12.5px;
  font-weight: 600;
}

.admin-pin .wv-search{
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  gap: .55rem;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid rgba(15,23,42,.06);
  min-width: 240px;
  max-width: 680px;
  flex: 1;
}
.admin-pin .wv-search i{ color: #6b7280; }
.admin-pin .wv-search input{
  border:none;
  outline:none;
  background:transparent;
  width:100%;
  font-size: 13px;
}


/* NEW */
.admin-pin.page-wrap{
  --wv-content-pad: 16px;

  /* ✅ width of your LEFT icon rail (from your screenshot ~116px) */
  --wv-rail-w: 125px;

  box-sizing: border-box;

  padding-top: 110px;
  padding-left: var(--wv-content-pad) !important;
  padding-right: var(--wv-content-pad) !important;

  background: #fafafa;
  min-height: 100vh;

  /* ✅ IMPORTANT: fit inside ONE visible line (no cut) */
  width: calc(100vw - var(--wv-rail-w));
  width: calc(100dvw - var(--wv-rail-w));
  max-width: calc(100vw - var(--wv-rail-w));
  max-width: calc(100dvw - var(--wv-rail-w));

  overflow-x: visible; /* ✅ do NOT clip the last card */
}

@media (max-width: 820px){
  .admin-pin.page-wrap{
    --wv-rail-w: 0px;
    width: 100vw;
    width: 100dvw;
    max-width: 100vw;
    max-width: 100dvw;
  }
}





/* NEW */
.admin-pin .wv-masonry{
  width: 100%;
  max-width: 100%;
  column-count: 5;
  column-gap: 14px;
}


  /* Spacer */

  /* ✅ True masonry (CSS columns) */

  .admin-pin .wv-masonry-item{
    break-inside: avoid;
    page-break-inside: avoid;
    -webkit-column-break-inside: avoid;
    display: inline-block;
    width: 100%;
    margin: 0 0 14px;
  }

  /* Pin card */
  .admin-pin .media-card{
    position: relative;
    /* ✅ Always visible border (not only on hover) */
    border: 1px solid rgba(15,23,42,.10);
    border-radius: 18px;
    background: rgba(255,255,255,.92);
    overflow: visible;
    /* ✅ subtle elevation even without hover */
    box-shadow: 0 10px 24px rgba(15,23,42,.08);
    transition: transform .16s ease, box-shadow .18s ease, border-color .18s ease;
    will-change: transform;
  }
  .admin-pin .media-card:hover{ 
    transform: translateY(-2px); 
    box-shadow: 0 18px 42px rgba(15,23,42,.14); 
    border-color: rgba(15,23,42,.16);
  }

  /* Links */
  .admin-pin .wv-pin-mainLink,
  .admin-pin .wv-pin-metaLink{ display:block; color:inherit; text-decoration:none; }
  .admin-pin .wv-pin-mainLink:focus-visible,
  .admin-pin .wv-pin-metaLink:focus-visible{ outline: 3px solid rgba(17,24,39,.25); outline-offset: 3px; border-radius: 18px; }

  /* ✅ Preview box: FULL media visible (no crop) with ORIGINAL ratio */
  .admin-pin .wv-pin-thumb-wrap{
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    background-color: #0b0f19; /* no white frames */

    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    /* ✅ Cap height so very tall media doesn’t create huge tiles */
    max-height: var(--wv-pin-maxh, 420px);

    /* aspect-ratio is set inline (measured per media) */
  }

  /* ✅ Center media-type icon (video/photo) */
  .admin-pin .wv-pin-typeIcon{
    position:absolute;
    inset:0;
    display:flex;
    align-items:center;
    justify-content:center;
    pointer-events:none;
    z-index: 3;
  }
  .admin-pin .wv-pin-typeIcon i{
    font-size: 36px;
    color: rgba(255,255,255,.92);
    filter: drop-shadow(0 10px 18px rgba(0,0,0,.55));
  }
  .admin-pin .wv-pin-thumb-wrap:not(.wv-pin-is-video) .wv-pin-typeIcon i{
    font-size: 28px;
    opacity: .78;
  }

  @media (max-width: 520px){
    .admin-pin .wv-pin-thumb-wrap{ max-height: min(var(--wv-pin-maxh, 420px), 340px); }
    .admin-pin .wv-pin-stats{ flex-wrap: wrap; }
  }

  .admin-pin .wv-pin-thumb{
    position: relative;
    z-index: 1;
    display:block;
    width: 100%;
    height: 100%;

    /* ✅ NO ZOOM / NO CROP */
    object-fit: contain !important;
    object-position: center !important;

    /* Avoid any inherited transforms */
    transform: none !important;

    background: transparent !important;
  }

  /* ✅ Hard override (in case global CSS touches media tags) */
  .admin-pin .wv-pin-thumb-wrap img,
  .admin-pin .wv-pin-thumb-wrap video{
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
    object-position: center !important;
    background: transparent !important;
    transform: none !important;
  }

  .admin-pin .wv-pin-skeleton{
    display:block;
    width:100%;
    height:100%;
    min-height: 160px;
    animation: wv-shimmer 1.2s infinite linear;
    background: linear-gradient(90deg, #0b0f19, #111827, #0b0f19);
    background-size: 200% 100%;
  }
  @keyframes wv-shimmer{ 0%{ background-position: 200% 0; } 100%{ background-position: -200% 0; } }

  /* Badges */
  .admin-pin .media-visibility,
  .admin-pin .media-kind,
  .admin-pin .media-final{
    position: absolute;
    top: 10px;
    padding: 5px 9px;
    border-radius: 999px;
    background: rgba(17,24,39,.62);
    color: #fff;
    font-size: 11.5px;
    font-weight: 500;
    letter-spacing: .2px;
    backdrop-filter: blur(6px);
    display: inline-flex;
    align-items: center;
    gap: .45rem;
    z-index: 2;
    border: 1px solid rgba(255,255,255,.12);
  }
  .admin-pin .media-visibility{ left: 10px; }
  .admin-pin .media-kind{ right: 10px; }
  .admin-pin .media-final{ left: 50%; transform: translateX(-50%); background: rgba(17,24,39,.78); }

  /* ✅ NEW: contributors stack overlay (FINAL only) */
  .admin-pin .wv-pin-contribStack{
    position: absolute;
    left: 12px;
    bottom: 12px;
    display: inline-flex;
    align-items: center;
    z-index: 3;
    pointer-events: none;
  }
  .admin-pin .wv-pin-contribAvatar,
  .admin-pin .wv-pin-contribMore{
    width: 34px;
    height: 34px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(255,255,255,.94);
    border: 2px solid rgba(255,255,255,.92);
    box-shadow: 0 10px 22px rgba(15,23,42,.22);
    display:flex;
    align-items:center;
    justify-content:center;
    margin-left: -10px;
  }
  .admin-pin .wv-pin-contribAvatar:first-child,
  .admin-pin .wv-pin-contribMore:first-child{
    margin-left: 0;
  }
  .admin-pin .wv-pin-contribAvatar img{
    width: 100%;
    height: 100%;
    object-fit: cover !important;
    display: block;
  }
  .admin-pin .wv-pin-contribMore{
    font-size: 12px;
    font-weight: 700;
    color: rgba(15,23,42,.92);
    background: rgba(255,255,255,.90);
  }
  @media (max-width: 420px){
    .admin-pin .wv-pin-contribAvatar,
    .admin-pin .wv-pin-contribMore{
      width: 30px;
      height: 30px;
      margin-left: -9px;
    }
  }


  .admin-pin .wv-pin-creatorAvatar img{ width:100%; height:100%; object-fit: cover !important; display:block; }

  /* Meta */

  .admin-pin .wv-pin-titleLink{
    flex: 1;
    min-width: 0;
  }



/* NEW */
.admin-pin .wv-pin-creatorAvatar{
  width: 28px;
  height: 28px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255,255,255,.95);
  border: 2px solid rgba(255,255,255,.92);
  box-shadow: 0 10px 22px rgba(15,23,42,.18);
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}

.admin-pin .wv-pin-titleRow{
  display:flex;
  align-items:center;
  gap: 8px;
  min-width:0;
  flex-wrap: nowrap;
}
/* NEW CODE (same feel as MyMedia: not bold) */
.admin-pin .wv-pin-title{
  font-weight: 500;   /* ✅ remove bold */
  font-size: 15px;    /* ✅ match MyMedia title size */
  line-height: 1.15;  /* ✅ match MyMedia */
  margin: 0;
  color: #0f172a;
}


.admin-pin .wv-pin-titleLoc{
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, .08);
  background: rgba(248, 250, 252, .9);
  color: rgba(15, 23, 42, .70);
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
  max-width: clamp(90px, 28%, 170px);
  overflow: hidden;
  min-width:0;
}

.admin-pin .wv-pin-titleLocText{
  min-width:0;
  overflow:hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

  .admin-pin .wv-pill-location{
    background:#f3f4f6;
    min-width: 0;
    max-width: clamp(140px, 28vw, 260px);
  }
  .admin-pin .wv-pill-location .wv-pill-text{
    display:inline-block;
    min-width: 0;
    max-width: clamp(90px, 20vw, 200px);
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }


.admin-pin .wv-pin-stats{
  display:flex;
  align-items:center;
  flex-wrap: nowrap;
  gap: 10px;
  margin-top: 6px;
  color: rgba(15, 23, 42, .68);
  font-size: 11px;
  min-width: 0;
  max-width: 100%;
}

/* prevent long location from pushing ❤️💬 out */
.admin-pin .wv-pill-location{
  max-width: clamp(120px, 22vw, 220px);
  min-width: 0;
}
.admin-pin .wv-pill-location .wv-pill-text{
  display:inline-block;
  max-width: 100%;
  overflow:hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-pin .wv-pill{
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  font-weight: 700;
  color: rgba(15, 23, 42, .70);
  white-space: nowrap;
  max-width: 100%;
  min-width: 0;
}
.admin-pin .wv-pill-date{
  flex: 1 1 auto;          /* ✅ can shrink so reactions stay visible */
  min-width: 0;
  overflow: hidden;
}
.admin-pin .wv-pill-date .wv-pill-text{
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

  /* Footer */
  .admin-pin .card-footer{ background: transparent !important; border:none !important; padding: 2px 6px 10px; color:#6b7280; }
  .admin-pin .wv-footer-inline{ display:flex; flex-wrap: wrap; align-items:center; gap: .55rem .9rem; }

  /* File fallback */
  .admin-pin .wv-pin-fileFallback{
    display:flex;
    align-items:center;
    justify-content:center;
    padding: 28px 10px;
    min-height: 160px;
    border-radius: 18px;
    background: #fff;
    border: 1px dashed rgba(15,23,42,.18);
    color: #6b7280;
    gap: .6rem;
  }
  `;

  const showInitialSpinner = filter !== "closed" && isLoading && mediaList.length === 0 && finals.length === 0;

  return (
    <div className="page-wrap admin-pin">
      <style>{styles}</style>

      {/* Top bar */}
      <div ref={topbarRef} className="wv-topbar">
        {filterOptions.map((opt) => (
          <div
            key={opt.id}
            className={`wv-chip ${filter === opt.id ? "wv-chip-active" : ""}`}
            onClick={() => setFilter(opt.id)}
            title={opt.label}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setFilter(opt.id);
            }}
          >
            <i className={opt.icon} />
            <span>{opt.label}</span>
          </div>
        ))}

       
      
        {/* ✅ "Open threads" smart sub-filter: keep ONE main filter, but still let user distinguish Video vs Image */}
        {filter === "open" ? (
          <>
            <span className="wv-divider" aria-hidden="true" />
            <div className="wv-subchips" role="group" aria-label="Open threads kind">
              <div
                className={`wv-chip wv-chip--sub ${openKind === "all" ? "wv-chip-active" : ""}`}
                onClick={() => setOpenKind("all")}
                title={t("open_kind_all")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setOpenKind("all");
                }}
              >
                <i className="bi bi-grid-3x3-gap" />
                <span>{t("open_kind_all")}</span>
              </div>

              <div
                className={`wv-chip wv-chip--sub ${openKind === "video" ? "wv-chip-active" : ""}`}
                onClick={() => setOpenKind("video")}
                title={t("open_kind_video")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setOpenKind("video");
                }}
              >
                <i className="bi bi-collection-play" />
                <span>{t("open_kind_video")}</span>
              </div>

              <div
                className={`wv-chip wv-chip--sub ${openKind === "image" ? "wv-chip-active" : ""}`}
                onClick={() => setOpenKind("image")}
                title={t("open_kind_image")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setOpenKind("image");
                }}
              >
                <i className="bi bi-image" />
                <span>{t("open_kind_image")}</span>
              </div>
            </div>
          </>
        ) : null}
</div>

  
<div className="filters-spacer" style={{ height: 20 }} />
      {/* Initial loader */}
      {showInitialSpinner ? (
        <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5">
          <div className="spinner-border mb-2" role="status">
            <span className="visually-hidden">{t("loading_short")}</span>
          </div>
          <small>{t("loading_media")}</small>
        </div>
      ) : (
        <div className="admin-pin-content pb-5">
          {filteredMedia.length ? (
            <div className="wv-masonry">
              {filteredMedia.map((item) => {
                const isFinal = item.__kind === "project-final";
                const cardTo = isFinal ? `/mediaDetails/final-${item.projectId}` : `/mediaDetails/${item.id}`;

                // Source / type
                let mediaURL = "";
                let isImage = false;
                let isVideo = false;
                let isFile = false;

                if (isFinal) {
                  mediaURL = item.final_src;
                  isVideo = true;
                } else {
                  const filePath = item.file_path || item.location || "";
                  const fromUploads = item.file_path !== undefined;
                  mediaURL = `${fromUploads ? "/uploads/" : "/output/"}${filePath}`;

                  const type = getMediaType(filePath);
                  isImage = type === "image";
                  isVideo = type === "video";
                  isFile = type === "file";
                }

                const visibility = item?.visibility;
// 👤 Creator
                const creatorUsername = String(resolveProjectCreatorUsername(item) || "").trim();
                const creatorProfilePath = creatorUsername ? resolveUserProfilePath(creatorUsername) : null;
                const creatorLabel = creatorUsername ? toCamelCaseName(resolveProjectCreatorLabel(item)) : "";
                const creatorAvatarSrc = creatorUsername ? avatarFor(creatorUsername, creatorLabel) : "/default-avatar.png";

                // 📅 Date INLINE
                const dateLabel = formatDate(item?.created_at);

                // ✅ NEW: contributors overlay for FINAL cards (real users from DB)
                const pid = isFinal ? String(item?.projectId || "").trim() : "";
                const rawContribs = pid ? projectContribsById[pid] : null;
                const owner = String(item?.user || creatorUsername || "").trim();

                const allContribs = (() => {
                  if (!isFinal) return [];
                  const base = Array.isArray(rawContribs) ? rawContribs : [];
                  const seen = new Set();
                  const out = [];

                  const push = (u) => {
                    const s = String(u || "").trim();
                    if (!s) return;
                    const k = s.toLowerCase();
                    if (seen.has(k)) return;
                    seen.add(k);
                    out.push(s);
                  };

                  push(owner);
                  base.forEach(push);

                  return out;
                })();

                // ✅ DEMO: enrich overlay with extra fake people (UI-only)
                const overlayContribs = isFinal ? buildContribOverlayList(pid, allContribs) : [];
                const shownContribs = isFinal ? overlayContribs.slice(0, 3) : [];
                const remainingContribs = isFinal ? Math.max(0, overlayContribs.length - shownContribs.length) : 0;

                return (
                  <div key={item.id} className="wv-masonry-item">
                    <div className="card text-dark media-card">
                      {/* Visual */}
                      <div className="position-relative">
                        <Link to={cardTo} className="wv-pin-mainLink" aria-label={t("open_aria", { title: item.title || t("generic_media") })}>
                          {isFinal && shownContribs.length ? (
                            <div className="wv-pin-contribStack" aria-label={t("contributors_count", { count: overlayContribs.length })}>
                              {shownContribs.map((c) => {
                                const label = String(c?.label || "").trim() || "User";
                                const src = String(c?.avatarSrc || "");
                                const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=dee2e6&color=495057`;

                                return (
                                  <span key={c.key} className="wv-pin-contribAvatar" title={label} aria-hidden="true">
                                    <img
                                      src={src || fallback}
                                      alt={label}
                                      loading="lazy"
                                      onError={(e) => {
                                        // If the demo faces CDN is blocked, fallback to initials avatar.
                                        try {
                                          e.currentTarget.onerror = null;
                                          e.currentTarget.src = fallback;
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                    />
                                  </span>
                                );
                              })}
                              {remainingContribs > 0 ? (
                                <span className="wv-pin-contribMore" aria-hidden="true">
                                  +{remainingContribs}
                                </span>
                              ) : null}
                            </div>
                          ) : null}

                          {isFile ? (
                            <div className="wv-pin-fileFallback">
                              <i className="bi bi-file-earmark-text" />
                              <span>Fichier</span>
                            </div>
                          ) : (
                            <LazyMediaVisual isImage={isImage} src={mediaURL} title={item.title} isVideo={isFinal || isVideo} />
                          )}
                        </Link>
                      </div>

                      {/* Meta */}
                      {/* Meta */}
<div className="card-body d-flex flex-column gap-1">
  {/* ✅ Avatar BEFORE title + ✅ Location inline on same row */}
  <div className="wv-pin-titleRow">
    {creatorUsername ? (
      creatorProfilePath ? (
        <Link
          to={creatorProfilePath}
          className="wv-pin-creatorAvatar"
          title={creatorLabel ? t("profile_title", { name: creatorLabel }) : t("profile")}
          aria-label={creatorLabel ? t("profile_title", { name: creatorLabel }) : t("profile")}
          onClick={(e) => e.stopPropagation()}
        >
          <img key={avatarBust} src={creatorAvatarSrc} alt={creatorLabel || t("creator")} loading="lazy" />
        </Link>
      ) : (
        <div className="wv-pin-creatorAvatar" title={creatorLabel || t("creator")} aria-label={creatorLabel || t("creator")}>
          <img key={avatarBust} src={creatorAvatarSrc} alt={creatorLabel || t("creator")} loading="lazy" />
        </div>
      )
    ) : null}

    <Link to={cardTo} className="wv-pin-metaLink wv-pin-titleLink" aria-label={t("media_details")}>
      <h6 className="wv-pin-title text-truncate" title={item.title}>
        {item.title}
      </h6>
    </Link>

    {item?.location ? (
      <span className="wv-pin-titleLoc" title={item.location}>
        <i className="bi bi-geo-alt" />
        <span className="wv-pin-titleLocText">{item.location}</span>
      </span>
    ) : null}
  </div>

  {/* ✅ One-line: date + reactions + comments */}
  <div className="wv-pin-stats">
    {dateLabel ? (
      <span className="wv-pill wv-pill-date" title={dateLabel}>
        <i className="bi bi-calendar-event" />
        <span className="wv-pill-text">{dateLabel}</span>
      </span>
    ) : null}
  </div>
</div>



{/* ✅ REMOVE footer block entirely (location moved above) */}

                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-muted py-5">{t("no_media")}</div>
          )}

          {/* Sentinel + loading hint */}
          <div ref={sentinelRef} />

          <div className="text-center text-muted py-4">
            {filter === "closed" ? null : isLoading ? <span>{t("loading_media")}</span> : hasMore ? <span>{t("scroll_more")}</span> : <span>{t("end_of_list")}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
